// src/lib/supabase.ts
/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.92, "tu3": 2, "notes": "Typed Supabase client bootstrap with PKCE auth options", "tuVersion": "2.5"}
 * .help "Creates the shared Supabase browser client from VITE_ env vars."
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';
import { AccessRole, ActivityRole } from '../types';

// Get environment variables (VITE_* are public client vars)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  const help = [
    'Missing Supabase environment variables.',
    'Create a .env file (or .env.local) with:',
    '  VITE_SUPABASE_URL=your-project-url',
    '  VITE_SUPABASE_ANON_KEY=your-anon-key',
    'You can copy .env.example and fill in values from Supabase Settings → API.',
  ].join('\n');
  console.error(help);
  throw new Error(help);
}

/**
 * Auth client
 * - detectSessionInUrl: false → we now handle:
 *    • Tozny (#id_token) via ToznyCallbackPage
 *    • Magic links (#type=magiclink or PKCE ?code=) via MagicLinkPage / OAuthCallbackPage
 *    • OAuth providers (?code=…) via OAuthCallbackPage
 *    • Password recovery (?code= / hash tokens) via UpdatePasswordPage — Auth UI
 *      update_password needs a session; skipping the exchange is "Auth session missing!"
 * - flowType: 'pkce' → GoTrue returns ?code= for OAuth, magic link, and recovery.
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // we parse & set sessions ourselves in the router pages
    flowType: 'pkce',          // <-- IMPORTANT: was 'implicit'; use PKCE for GitHub/Google/Microsoft
  },
  db: { schema: 'public' },
});

// Minimal dev log without exposing sensitive data
if (import.meta.env.DEV) {
  void supabase.auth.getSession().then(({ data, error }) => {
    if (error) console.warn('supabase.getSession (dev):', error);
    else console.log('supabase.getSession (dev):', data.session ? 'SESSION PRESENT' : 'NO SESSION');
  });
}

export const initializeUser = async () => {
  console.log('initializeUser: Starting...');
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error('initializeUser: Error getting user from auth or no user found.', userError);
      return { dbUser: null };
    }

    console.log(`initializeUser: User found: ${user.id}. Fetching profile from public.users...`);

    // First try to fetch with is_dark_mode column
    let userData, selectError;
    try {
      const result = await supabase
        .from('users')
        .select(`
          id,
          email,
          name,
          first_name,
          last_name,
          avatar_url,
          in_flow_state,
          enable_background_animation,
          animation_intensity,
          background_type,
          warp_speed,
          headline_feed_bundle,
          headline_feed_keyword,
          backplane_pin_enabled,
          backplane_desk_cleared,
          is_dark_mode,
          timezone,
          work_city,
          work_lat,
          work_lng,
          is_agent,
          agent_type,
          llm_model,
          agent_instructions,
          agent_current_period_tokens_used,
          agent_token_limit_per_period,
          agent_last_token_reset_date,
          agent_last_token_warning_sent,
          agent_user_observations,
          agent_capabilities,
          casual_name,
          bio,
          skills,
          fun_fact,
          pronouns
        `)
        .eq('id', user.id)
        .single();
      userData = result.data;
      selectError = result.error;
    } catch (error) {
      // If warp_speed or is_dark_mode column doesn't exist, fetch without them
      console.log('initializeUser: warp_speed or is_dark_mode column not found, fetching without them...');
      const result = await supabase
        .from('users')
        .select(`
          id,
          email,
          name,
          first_name,
          last_name,
          avatar_url,
          in_flow_state,
          enable_background_animation,
          animation_intensity,
          background_type,
          timezone,
          is_agent,
          agent_type,
          llm_model,
          agent_instructions,
          agent_current_period_tokens_used,
          agent_token_limit_per_period,
          agent_last_token_reset_date,
          agent_last_token_warning_sent,
          agent_user_observations,
          agent_capabilities,
          casual_name,
          bio,
          skills,
          fun_fact,
          pronouns
        `)
        .eq('id', user.id)
        .single();
      userData = result.data;
      selectError = result.error;
    }

    let dbUser = userData as (typeof userData & {
      warp_speed?: number | null;
      headline_feed_bundle?: string | null;
      headline_feed_keyword?: string | null;
      is_dark_mode?: boolean | null;
      timezone?: string | null;
      work_city?: string | null;
      work_lat?: number | null;
      work_lng?: number | null;
    }) | null;

    console.log('🔍 initializeUser: dbUser from database:', {
      bio: dbUser?.bio,
      skills: dbUser?.skills,
      fun_fact: dbUser?.fun_fact,
      pronouns: dbUser?.pronouns
    });

    if (selectError) {
      console.error('initializeUser: Error fetching user profile from public.users:', selectError);
      if (selectError.code === 'PGRST116') {
        console.log('initializeUser: User profile not found in public.users, creating basic entry...');
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: user.id,
            email: user.email,
            name: user.email?.split('@')[0] || 'Anonymous',
            first_name: null,
            last_name: null,
            enable_background_animation: true,
            background_type: 'tide-breath',
          })
          .select()
          .single();

        if (insertError) {
          console.error('initializeUser: Failed to create basic user profile in public.users:', insertError);
          return { dbUser: null };
        }
        dbUser = newUser;
      } else {
        return { dbUser: null };
      }
    }

    console.log('initializeUser: Basic profile from public.users fetched/created successfully.');

    console.log(`initializeUser: Fetching membership for user ${user.id}...`);
    const { data: membershipData, error: membershipError } = await supabase
      .from('memberships')
      .select('*')
      .eq('user_id', user.id)
      .single();

    let finalDbUser = {
      id: user.id,
      email: user.email,
      name: dbUser?.name || user.email?.split('@')[0] || 'Anonymous',
      firstName: dbUser?.first_name || undefined,
      lastName: dbUser?.last_name || undefined,
      avatar: dbUser?.avatar_url || null,
      inFlowState: dbUser?.in_flow_state || false,
      enableBackgroundAnimation: dbUser?.enable_background_animation || false,
      animationIntensity: dbUser?.animation_intensity || 0.2,
      backgroundType: dbUser?.background_type || 'network',
      warpSpeed: dbUser?.warp_speed || 5, // Default to 5 if column doesn't exist
      headlineFeedBundle: dbUser?.headline_feed_bundle || 'mixed',
      headlineFeedKeyword: dbUser?.headline_feed_keyword || null,
      backplanePinEnabled: !!(dbUser as { backplane_pin_enabled?: boolean } | null)?.backplane_pin_enabled,
      backplaneDeskCleared: !!(dbUser as { backplane_desk_cleared?: boolean } | null)?.backplane_desk_cleared,
      timezone: dbUser?.timezone || null,
      workCity: dbUser?.work_city || null,
      workLat: typeof dbUser?.work_lat === 'number' ? dbUser.work_lat : null,
      workLng: typeof dbUser?.work_lng === 'number' ? dbUser.work_lng : null,
      // Use database value if it exists, otherwise default to true (dark mode)
      // Note: null in database means user hasn't set a preference yet, so we default to dark mode
      isDarkMode: dbUser?.is_dark_mode !== null && dbUser?.is_dark_mode !== undefined 
        ? dbUser.is_dark_mode 
        : true,

      isAgent: dbUser?.is_agent || false,
      agentType: dbUser?.agent_type || null,
      llmModel: dbUser?.llm_model || null,
      agentInstructions: dbUser?.agent_instructions || null,
      agentCurrentPeriodTokensUsed: dbUser?.agent_current_period_tokens_used || 0,
      agentTokenLimitPerPeriod: dbUser?.agent_token_limit_per_period || 100000,
      agentLastTokenResetDate: dbUser?.agent_last_token_reset_date || null,
      agentLastTokenWarningSent: dbUser?.agent_last_token_warning_sent || null,
      agentUserObservations: dbUser?.agent_user_observations || null,
      agentCapabilities: dbUser?.agent_capabilities || null,
      casualName: dbUser?.casual_name || undefined,
      bio: dbUser?.bio || undefined,
      skills: dbUser?.skills || undefined,
      funFact: dbUser?.fun_fact || undefined,
      pronouns: dbUser?.pronouns || undefined,

      accessRole: 'member' as AccessRole,
      activityRoles: [] as ActivityRole[],
      reportsTeamId: null as string | null,
      companyId: null as string | null,
      team: null as string | null,
    };

    if (membershipError) {
      if (membershipError.code === 'PGRST116') {
        console.log('initializeUser: No membership found for this user. Assigning default roles.');
      } else {
        console.error('initializeUser: Error fetching membership data:', membershipError);
      }
    } else if (membershipData) {
      console.log('initializeUser: Membership found. Applying roles and company_id from membership.');
      finalDbUser.accessRole = membershipData.access_role as AccessRole;
      finalDbUser.activityRoles = (membershipData.activity_roles ?? []) as ActivityRole[];
      finalDbUser.reportsTeamId = membershipData.reports_team_id ?? null;
      finalDbUser.companyId = membershipData.company_id;
      finalDbUser.team = membershipData.team_id;
    }

    console.log('initializeUser: Final dbUser object constructed:', finalDbUser);
    return { dbUser: finalDbUser };
  } catch (error) {
    console.error('initializeUser: An unexpected error occurred:', error);
    return { dbUser: null };
  }
};

/**
 * December 5, 2025: Get GitHub token from all possible sources
 * Returns the token if found and valid, null otherwise
 * 
 * Priority order:
 * 1. Company GitHub App token (if companyId provided)
 * 2. User's personal token from identity_data, session, or user_metadata
 * 
 * @param companyId - Optional company ID to check for GitHub App installation first
 * @returns { token, source } where source is 'app' or 'personal'
 */
export const getGitHubToken = async (companyId?: string): Promise<string | null> => {
  // First, try company GitHub App (preferred method)
  if (companyId) {
    try {
      console.log('getGitHubToken: Checking for GitHub App token...');
      const { data, error } = await supabase.functions.invoke('github-app', {
        body: { action: 'get_token', company_id: companyId },
      });
      
      if (!error && data?.success && data?.token) {
        console.log('getGitHubToken: ✅ Using GitHub App token for', data.account_login);
        return data.token;
      }
      
      if (data?.needs_install) {
        console.log('getGitHubToken: GitHub App not installed for this company');
      }
    } catch (err) {
      console.warn('getGitHubToken: GitHub App token check failed:', err);
    }
  }
  
  // Fall back to user's personal token
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const githubIdentity = user?.identities?.find(i => i.provider === 'github');
    
    if (!githubIdentity) {
      console.log('getGitHubToken: No GitHub identity found');
      return null;
    }
    
    // Try identity_data first
    let token = githubIdentity.identity_data?.provider_token;
    
    // Try session provider_token
    if (!token) {
      const { data: sessionData } = await supabase.auth.getSession();
      token = sessionData.session?.provider_token;
    }
    
    // Try user_metadata (our fallback storage)
    if (!token) {
      token = user?.user_metadata?.github_token;
      if (token) {
        console.log('getGitHubToken: Using token from user_metadata');
      }
    }
    
    if (!token) {
      console.log('getGitHubToken: No personal token found');
      return null;
    }
    
    // Validate the token is still working
    const response = await fetch('https://api.github.com/user', {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!response.ok) {
      console.warn('getGitHubToken: Personal token invalid, API returned:', response.status);
      return null;
    }
    
    console.log('getGitHubToken: ✅ Using personal GitHub token');
    return token;
  } catch (error) {
    console.error('getGitHubToken: Error:', error);
    return null;
  }
};

/**
 * Get GitHub repos available for linking
 * 
 * SIMPLE APPROACH:
 * 1. User connects personal GitHub OAuth (has their own permissions)
 * 2. We get the company's GitHub org name (from GitHub App installation if exists)
 * 3. Filter user's repos to ONLY that org
 * 
 * This shows: Only company repos the user personally has access to
 */
export const getGitHubRepos = async (companyId?: string): Promise<{ 
  repos: any[], 
  source: 'app' | 'personal' | 'filtered' | null,
  needsPersonalGitHub?: boolean,
  message?: string,
  orgName?: string,
}> => {
  // Step 1: Get company's GitHub org name (from GitHub App installation)
  let companyOrgName: string | null = null;
  
  if (companyId) {
    try {
      const { data: installation } = await supabase
        .from('github_app_installations')
        .select('account_login')
        .eq('company_id', companyId)
        .single();
      
      if (installation?.account_login) {
        companyOrgName = installation.account_login;
        console.log('getGitHubRepos: Company org is', companyOrgName);
      }
    } catch (err) {
      console.log('getGitHubRepos: No GitHub App installation found for company');
    }
  }
  
  // Step 2: Get user's personal GitHub repos
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const githubIdentity = user?.identities?.find(i => i.provider === 'github');
    
    if (!githubIdentity) {
      return { 
        repos: [], 
        source: null,
        message: 'Connect your GitHub account in Profile Settings to link repositories.',
      };
    }
    
    // Get personal token
    let token = githubIdentity.identity_data?.provider_token;
    if (!token) {
      const { data: sessionData } = await supabase.auth.getSession();
      token = sessionData.session?.provider_token;
    }
    if (!token) {
      token = user?.user_metadata?.github_token;
    }
    
    if (!token) {
      return { 
        repos: [], 
        source: null,
        message: 'GitHub token expired. Please reconnect GitHub in Profile Settings.',
      };
    }
    
    // Fetch user's repos (all they have access to)
    const response = await fetch('https://api.github.com/user/repos?sort=updated&per_page=100&affiliation=owner,collaborator,organization_member', {
      headers: { Authorization: `token ${token}` },
    });
    
    if (!response.ok) {
      return { 
        repos: [], 
        source: null,
        message: 'Failed to fetch GitHub repos. Token may be expired.',
      };
    }
    
    const allRepos = await response.json();
    console.log('getGitHubRepos: User has access to', allRepos.length, 'total repos');
    
    // Step 3: Filter to company org (if we know it)
    if (companyOrgName) {
      const filteredRepos = allRepos.filter((repo: any) => 
        repo.owner.login.toLowerCase() === companyOrgName!.toLowerCase()
      );
      
      console.log('getGitHubRepos: ✅ Filtered to', companyOrgName, ':', filteredRepos.length, 'repos');
      
      return {
        repos: filteredRepos,
        source: 'filtered',
        orgName: companyOrgName,
        message: filteredRepos.length === 0 
          ? `No ${companyOrgName} repos found. Make sure you have access to org repositories.`
          : undefined,
      };
    }
    
    // No org name known - show all repos with warning
    console.log('getGitHubRepos: ⚠️ No company org configured, showing all', allRepos.length, 'repos');
    return {
      repos: allRepos,
      source: 'personal',
      message: 'Showing all your repos. Admin can install GitHub App to filter by organization.',
    };
    
  } catch (err) {
    console.error('getGitHubRepos: Error:', err);
    return { 
      repos: [], 
      source: null,
      message: 'Failed to fetch GitHub repos.',
    };
  }
};